'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SortOption {
  id: string;
  label: string;
}

interface SortDropdownProps {
  sortBy: string;
  onSortChange: (sortId: string) => void;
}

const SortDropdown = ({ sortBy, onSortChange }: SortDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sortOptions: SortOption[] = [
    { id: 'updated-desc', label: 'Recently Updated' },
    { id: 'created-desc', label: 'Newest First' },
    { id: 'created-asc', label: 'Oldest First' },
    { id: 'title-asc', label: 'Title (A-Z)' },
    { id: 'title-desc', label: 'Title (Z-A)' },
    { id: 'entries-desc', label: 'Most Entries' },
    { id: 'entries-asc', label: 'Least Entries' },
  ];

  const currentSort = sortOptions.find((opt) => opt.id === sortBy) || sortOptions[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSortSelect = (sortId: string) => {
    onSortChange(sortId);
    setIsOpen(false);
  };

  return (
    <div data-component-id="src/app/projects-index/components/SortDropdown.tsx:54:4" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="54" data-component-end-line="93" data-component-file="SortDropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" ref={dropdownRef} className="relative">
      <button data-component-id="src/app/projects-index/components/SortDropdown.tsx:55:6" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="55" data-component-end-line="67" data-component-file="SortDropdown.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-4%20py-2.5%20rounded-lg%20border%20border-border%20bg-card%20text-foreground%20font-medium%20transition-smooth%20hover%3Abg-muted%22%7D"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2.5 rounded-lg border border-border bg-card text-foreground font-medium transition-smooth hover:bg-muted"
      >
        <Icon data-component-id="src/app/projects-index/components/SortDropdown.tsx:59:8" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="59" data-component-end-line="59" data-component-file="SortDropdown.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowsUpDownIcon%22%7D" name="ArrowsUpDownIcon" size={20} />
        <span data-component-id="src/app/projects-index/components/SortDropdown.tsx:60:8" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="60" data-component-end-line="60" data-component-file="SortDropdown.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hidden%20sm%3Ainline%22%7D" className="hidden sm:inline">{currentSort.label}</span>
        <span data-component-id="src/app/projects-index/components/SortDropdown.tsx:61:8" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="61" data-component-end-line="61" data-component-file="SortDropdown.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22sm%3Ahidden%22%2C%22textContent%22%3A%22Sort%22%7D" className="sm:hidden">Sort</span>
        <Icon data-component-id="src/app/projects-index/components/SortDropdown.tsx:62:8" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="62" data-component-end-line="66" data-component-file="SortDropdown.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D"
          name="ChevronDownIcon"
          size={16}
          className={`transition-smooth ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div data-component-id="src/app/projects-index/components/SortDropdown.tsx:70:8" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="70" data-component-end-line="91" data-component-file="SortDropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20right-0%20mt-2%20w-56%20bg-card%20rounded-lg%20border%20border-border%20shadow-editorial-lg%20z-50%22%7D" className="absolute right-0 mt-2 w-56 bg-card rounded-lg border border-border shadow-editorial-lg z-50">
          <div data-component-id="src/app/projects-index/components/SortDropdown.tsx:71:10" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="71" data-component-end-line="90" data-component-file="SortDropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-2%22%7D" className="py-2">
            {sortOptions.map((option) => (
              <button data-component-id="src/app/projects-index/components/SortDropdown.tsx:73:14" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="73" data-component-end-line="88" data-component-file="SortDropdown.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={option.id}
                onClick={() => handleSortSelect(option.id)}
                className={`w-full text-left px-4 py-2.5 transition-smooth ${
                  sortBy === option.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <div data-component-id="src/app/projects-index/components/SortDropdown.tsx:82:16" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="82" data-component-end-line="87" data-component-file="SortDropdown.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
                  <span data-component-id="src/app/projects-index/components/SortDropdown.tsx:83:18" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="83" data-component-end-line="83" data-component-file="SortDropdown.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{option.label}</span>
                  {sortBy === option.id && (
                    <Icon data-component-id="src/app/projects-index/components/SortDropdown.tsx:85:20" data-component-path="src/app/projects-index/components/SortDropdown.tsx" data-component-line="85" data-component-end-line="85" data-component-file="SortDropdown.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={16} />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SortDropdown;